import React, { useEffect, useRef, useState } from "react";

/**
 * Reveals its children with a subtle fade-up once they scroll into view.
 * Falls back to instantly-visible content for users who prefer reduced
 * motion (handled in index.css) or browsers without IntersectionObserver.
 */
const Reveal = ({
    children,
    as: Tag = "div",
    delay = 0,
    className = "",
    ...rest
}) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const node = ref.current;
        if (!node || typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisible(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    const delayClass = delay ? ` reveal-delay-${delay}` : "";

    return (
        <Tag
            ref={ref}
            className={`reveal${visible ? " is-visible" : ""}${delayClass} ${className}`.trim()}
            {...rest}
        >
            {children}
        </Tag>
    );
};

export default Reveal;
